/**
 * Per-page overlay layer for review pins.
 *
 * Mounted by `DocumentViewer`'s `renderOverlay` callback, so this component
 * sees one `(page, scale, rotation, rendered)` tuple at a time. We project
 * each comment / pending suggestion's anchor into rendered-view space and
 * draw a small pin. Clicking a pin calls `onFocus(uuid)` which the parent
 * page wires up to the side panel via `ReviewFocusProvider`.
 *
 * Pins use `pointer-events-auto` to override the viewer's
 * `pointer-events-none` overlay wrapper — the rest of the wrapper still
 * lets mouse-wheel scrolling through, which is what we want.
 */
import { useMemo } from 'react'
import { MessageSquare, ListChecks, CheckCircle2 } from 'lucide-react'

import {
  documentToView,
  documentRectToView,
  type Rect,
} from '@/components/docfiller/viewer/coordinates'
import type {
  PageDimensions,
  Rotation,
} from '@/components/docfiller/viewer/types'
import type {
  DocumentComment,
  DocumentSuggestion,
  Field,
} from '@/api/docfiller-types'
import { cn } from '@/lib/utils'

import { useReviewFocus } from './useReviewFocus'

interface PinPosition {
  x: number
  y: number
  /** Optional region rect in rendered view coords (for region anchors). */
  rect?: Rect
}

function anchorToPin(
  anchor:
    | DocumentComment['anchor']
    | NonNullable<DocumentSuggestion['anchor']>,
  fields: Field[],
  dims: PageDimensions,
  scale: number,
  rotation: Rotation,
): PinPosition | null {
  if (anchor.type === 'region' && anchor.rect) {
    const r = anchor.rect as Record<string, number>
    if (
      typeof r.x !== 'number' ||
      typeof r.y !== 'number' ||
      typeof r.width !== 'number' ||
      typeof r.height !== 'number'
    ) {
      return null
    }
    const rect = documentRectToView(
      { x: r.x, y: r.y, width: r.width, height: r.height },
      dims,
      scale,
      rotation,
    )
    return { x: rect.x, y: rect.y, rect }
  }
  if (anchor.type === 'field' && anchor.field_uuid) {
    const field = fields.find((f) => f.uuid === anchor.field_uuid)
    if (!field?.anchor) return null
    const fa = field.anchor as Record<string, unknown>
    const x = typeof fa.x === 'number' ? fa.x : 0
    const y = typeof fa.y === 'number' ? fa.y : 0
    const p = documentToView({ x, y }, dims, scale, rotation)
    return { x: p.x, y: p.y }
  }
  return null
}

export interface ReviewOverlayProps {
  pageNumber: number
  dims: PageDimensions
  scale: number
  rotation: Rotation
  fields: Field[]
  threads: { root: DocumentComment }[]
  pendingSuggestions: DocumentSuggestion[]
  /** Recently focused pin uuid — drives the "selected" pulse styling. */
  focusedUuid?: string | null
  /**
   * Bridge from a pin click back to the parent — the parent updates the
   * shared `ReviewFocusProvider` which both surfaces subscribe to.
   */
  onFocusComment?: (uuid: string) => void
  onFocusSuggestion?: (uuid: string) => void
}

export function ReviewOverlay({
  pageNumber,
  dims,
  scale,
  rotation,
  fields,
  threads,
  pendingSuggestions,
  focusedUuid,
  onFocusComment,
  onFocusSuggestion,
}: ReviewOverlayProps) {
  const focusFromCtx = useReviewFocus()
  const effectiveFocused = focusedUuid ?? focusFromCtx.focused?.uuid ?? null

  const commentPins = useMemo(() => {
    return threads
      .filter((t) => t.root.anchor.page === pageNumber)
      .map((t) => ({
        thread: t,
        pin: anchorToPin(t.root.anchor, fields, dims, scale, rotation),
      }))
      .filter(
        (entry): entry is { thread: { root: DocumentComment }; pin: PinPosition } =>
          entry.pin !== null,
      )
  }, [threads, fields, dims, scale, rotation, pageNumber])

  const suggestionPins = useMemo(() => {
    return pendingSuggestions
      .filter((s) => (s.anchor?.page ?? 1) === pageNumber)
      .map((s) => {
        // A suggestion may carry its own anchor; if it doesn't, fall back to
        // the field's own anchor so the pin still lands somewhere sensible.
        const anchor = s.anchor ?? {
          type: 'field' as const,
          page: pageNumber,
          field_uuid: s.field_uuid,
          rect: null,
        }
        return {
          suggestion: s,
          pin: anchorToPin(anchor, fields, dims, scale, rotation),
        }
      })
      .filter(
        (entry): entry is { suggestion: DocumentSuggestion; pin: PinPosition } =>
          entry.pin !== null,
      )
  }, [pendingSuggestions, fields, dims, scale, rotation, pageNumber])

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      data-testid="review-overlay"
      data-page-number={pageNumber}
    >
      {commentPins.map(({ thread, pin }) => {
        const isResolved = thread.root.is_resolved
        const isFocused = effectiveFocused === thread.root.uuid
        return (
          <button
            key={`c-${thread.root.uuid}`}
            type="button"
            className={cn(
              'pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2',
              'inline-flex h-6 w-6 items-center justify-center rounded-full border',
              'shadow-soft transition-colors focus:outline-none',
              'focus-visible:ring-2 focus-visible:ring-ring/40',
              isResolved
                ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-700'
                : 'border-amber-400/60 bg-amber-400/20 text-amber-800',
              isFocused && 'ring-2 ring-primary/50 scale-110',
            )}
            style={{ left: `${pin.x}px`, top: `${pin.y}px` }}
            data-testid="review-pin-comment"
            data-comment-uuid={thread.root.uuid}
            aria-label={
              isResolved
                ? `Resolved comment by ${thread.root.author_name}`
                : `Open comment by ${thread.root.author_name}`
            }
            onClick={(e) => {
              e.stopPropagation()
              if (onFocusComment) onFocusComment(thread.root.uuid)
              else
                focusFromCtx.focus({
                  kind: 'comment',
                  uuid: thread.root.uuid,
                  page: pageNumber,
                })
            }}
          >
            {isResolved ? (
              <CheckCircle2 className="size-3.5" aria-hidden />
            ) : (
              <MessageSquare className="size-3.5" aria-hidden />
            )}
          </button>
        )
      })}
      {suggestionPins.map(({ suggestion, pin }) => {
        const isFocused = effectiveFocused === suggestion.uuid
        return (
          <button
            key={`s-${suggestion.uuid}`}
            type="button"
            className={cn(
              'pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2',
              'inline-flex h-6 w-6 items-center justify-center rounded-full border',
              'border-sky-400/60 bg-sky-400/20 text-sky-700 shadow-soft',
              'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
              isFocused && 'ring-2 ring-primary/50 scale-110',
            )}
            style={{ left: `${pin.x}px`, top: `${pin.y}px` }}
            data-testid="review-pin-suggestion"
            data-suggestion-uuid={suggestion.uuid}
            aria-label={`Pending suggestion for ${suggestion.field_friendly_name ?? suggestion.field_name ?? 'field'}`}
            onClick={(e) => {
              e.stopPropagation()
              if (onFocusSuggestion) onFocusSuggestion(suggestion.uuid)
              else
                focusFromCtx.focus({
                  kind: 'suggestion',
                  uuid: suggestion.uuid,
                  page: pageNumber,
                })
            }}
          >
            <ListChecks className="size-3.5" aria-hidden />
          </button>
        )
      })}
    </div>
  )
}
